import { useEffect, useRef, useState } from 'react';

import { TrackingEventsV2 } from '@/analytics/eventsV2';
import { trackCustomEvent } from '@/analytics/Mixpanel';
import { Icon } from '@/new/components/icon';
import { Input } from '@/new/components/shadcn/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/new/components/shadcn/select';

interface LogicalSpeaker {
  id: string;
  name: string;
  color: string;
}

interface InternalSpeaker {
  id: string;
  internalName: string;
  sessionNumber: number;
  displayName: string;
  logicalSpeakerId?: string;
  logicalSpeakerName?: string;
  logicalSpeakerColor?: string;
}

interface SpeakersPanelProps {
  logicalSpeakers: LogicalSpeaker[];
  internalSpeakers: InternalSpeaker[];
  editingSpeaker: string | null;
  editingName: string;
  onStartEditing: (speaker: LogicalSpeaker | InternalSpeaker) => void;
  onCancelEditing: () => void;
  onSaveSpeaker: (speakerId: string, name: string) => void;
  onEditingNameChange: (name: string) => void;
  deponent?: string | null;
  otherSpeakers?: string[];
}

const getSpeakerDisplayInfo = (speaker: LogicalSpeaker | InternalSpeaker) => {
  const isLogical = 'name' in speaker;

  return {
    name: isLogical ? speaker.name : speaker.displayName,
    color: isLogical ? speaker.color : speaker.logicalSpeakerColor || 'gray',
    isLogical,
  };
};

export const SpeakersPanel = ({
  logicalSpeakers,
  internalSpeakers,
  editingSpeaker,
  editingName,
  onStartEditing,
  onCancelEditing,
  onSaveSpeaker,
  onEditingNameChange,
  deponent,
  otherSpeakers,
}: SpeakersPanelProps) => {
  const [showCustomInput, setShowCustomInput] = useState<string | null>(null);

  const predefinedSpeakers = [
    ...(deponent ? [{ value: deponent, label: `${deponent} (Deponent)` }] : []),
    ...(otherSpeakers || []).map((speaker) => ({ value: speaker, label: speaker })),
  ];

  // Sort logical speakers with deponent first
  const sortedLogicalSpeakers = logicalSpeakers.toSorted((a) => (a.name === deponent ? -1 : 1));

  // Combine logical and internal speakers for display
  const allSpeakers = [
    ...sortedLogicalSpeakers.map((speaker) => ({ ...speaker, type: 'logical' as const })),
    ...internalSpeakers.map((speaker) => ({ ...speaker, type: 'internal' as const })),
  ];

  const handleCustomInputKeyDown = (speakerId: string, e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && editingName) {
      trackCustomEvent(TrackingEventsV2.RealtimeAssignSpeaker);
      onSaveSpeaker(speakerId, editingName);
      setShowCustomInput(null);
    } else if (e.key === 'Escape') {
      setShowCustomInput(null);
      onCancelEditing();
    }
  };

  const handleCustomInputBlur = (speakerId: string) => {
    if (editingName) {
      trackCustomEvent(TrackingEventsV2.RealtimeAssignSpeaker);
      onSaveSpeaker(speakerId, editingName);
    }
    setShowCustomInput(null);
    if (!editingName) {
      onCancelEditing();
    }
  };

  const handleSelectChange = (speaker: LogicalSpeaker | InternalSpeaker, value: string) => {
    if (value === '__custom__') {
      onStartEditing(speaker);
      setShowCustomInput(speaker.id);
      onEditingNameChange('');
      return;
    }

    const { name } = getSpeakerDisplayInfo(speaker);
    if (name !== value) {
      trackCustomEvent(TrackingEventsV2.RealtimeAssignSpeaker);
      onStartEditing(speaker);
      onEditingNameChange(value);
      onSaveSpeaker(speaker.id, value);
    }
  };

  const handleInputChange = (speaker: LogicalSpeaker | InternalSpeaker, value: string) => {
    onStartEditing(speaker);
    onEditingNameChange(value);
  };

  // For applying the fade effect
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [isScrolledToBottom, setIsScrolledToBottom] = useState(false);

  // Check if scrolled to bottom on mount
  useEffect(() => {
    const target = scrollContainerRef.current;
    if (target) {
      setIsScrolledToBottom(target.scrollWidth - target.scrollLeft <= target.clientWidth + 1);
    }
  }, [allSpeakers]);

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const target = e.target as HTMLElement;
    setIsScrolledToBottom(target.scrollWidth - target.scrollLeft <= target.clientWidth + 1);
  };

  return (
    <div className="sticky top-2 left-0 z-20 m-2 flex w-fit max-w-[calc(100%_-_1rem)] gap-4 rounded-lg border bg-gray-50 px-4">
      <p className="mr-4 flex items-center gap-2.5 text-gray-500">
        <Icon className="lucide--user-round" />
        <span>Speakers</span>
      </p>

      <Icon
        className={`${isScrolledToBottom ? 'hidden' : 'inline'} lucide--arrow-right absolute top-1/2 right-4 z-20 -translate-y-1/2 text-gray-500 duration-150`}
      />

      <div
        className="relative flex w-full gap-5 overflow-auto mask-r-from-85% mask-r-to-97% py-3 transition-[mask-size] duration-200"
        style={{ maskSize: isScrolledToBottom ? '115% 100%' : '100% 100%', scrollbarWidth: 'none' }}
        onScroll={handleScroll}
        ref={scrollContainerRef}
      >
        {allSpeakers.toReversed().map((speaker) => {
          const { name, color } = getSpeakerDisplayInfo(speaker);

          const isEditing = editingSpeaker === speaker.id;
          const isCustomInput = showCustomInput === speaker.id;
          const currentValue = isEditing ? editingName : name;

          return (
            <div
              key={speaker.id}
              className={`group flex items-center gap-2.5 rounded-lg duration-150 border-${color}-200 hover:border-gray-300`}
            >
              {/* Dot */}
              <div
                className={`size-3 border-3 border-${color}-200 shrink-0 rounded-full bg-${color}-400 ring-1 ring-white`}
              />

              {isCustomInput ? (
                <Input
                  type="text"
                  value={isEditing ? editingName : ''}
                  onChange={(e) => handleInputChange(speaker, e.target.value)}
                  onKeyDown={(e) => handleCustomInputKeyDown(speaker.id, e)}
                  onBlur={() => handleCustomInputBlur(speaker.id)}
                  placeholder="Enter name..."
                  className="-my-2 min-w-28"
                  autoFocus
                />
              ) : (
                <Select value={currentValue} onValueChange={(v) => handleSelectChange(speaker, v)}>
                  <SelectTrigger className={`h-auto max-w-34 border-none p-0 text-gray-700`}>
                    <SelectValue />
                  </SelectTrigger>

                  <SelectContent>
                    {predefinedSpeakers.map((p) => (
                      <SelectItem key={p.value} value={p.value}>
                        {p.label}
                      </SelectItem>
                    ))}
                    {/* This is a bit of a temporary fix as for some reason 'other speakers' aren't included in predefined speakers, and if they are, they don't work */}
                    {name && !predefinedSpeakers.find((p) => p.value === name) && (
                      <SelectItem value={name}>{name}</SelectItem>
                    )}
                    <SelectItem value="__custom__">Enter custom name...</SelectItem>
                  </SelectContent>
                </Select>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};
